import React, { FC, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { createImportRemoteFailedListener } from '@adsk/exo-skeleton-api';

import { useLiveFeatureFlag } from './LaunchDarklyProvider';
import useFeatureFlagOnce from '../hooks/useFeatureFlagOnce';
import {
  clearServiceWorkerCache,
  initializeServiceWorker,
} from '../utils/runtime';

const FEATURE_FLAG_NAME = 'acc-exo-skeleton-service-worker';
const PURGE_SERVICE_WORKER_CACHE_FEATURE_FLAG = 'service-worker-purge-cache';
export const ServiceWorkerContainer: FC = ({ children }) => {
  const { value, loading } = useFeatureFlagOnce(FEATURE_FLAG_NAME, false);
  const purgeServiceWorkerCache = useLiveFeatureFlag(
    PURGE_SERVICE_WORKER_CACHE_FEATURE_FLAG,
    false,
  );

  const purgeServiceWorkerCacheRef = useRef<{
    value: boolean | undefined;
    loading: boolean;
  }>(purgeServiceWorkerCache);

  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    purgeServiceWorkerCacheRef.current = purgeServiceWorkerCache;
  }, [purgeServiceWorkerCache]);

  const PurgeCacheIfNecessary = useMemo(() => {
    return () => {
      const {
        value: purgeServiceWorkerCacheValue,
        loading: purgeServiceWorkerCacheLoading,
      } = purgeServiceWorkerCacheRef.current;
      if (!purgeServiceWorkerCacheLoading && purgeServiceWorkerCacheValue) {
        clearServiceWorkerCache();
      }
      if (purgeServiceWorkerCacheLoading) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          PurgeCacheIfNecessary();
        }, 1000);
      }
    };
  }, []);

  useEffect(() => {
    const listener = createImportRemoteFailedListener({
      onImportRemoteFailed: () => {
        PurgeCacheIfNecessary();
      },
    });

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      listener?.dispose?.();
    };
  }, [PurgeCacheIfNecessary]);

  useLayoutEffect(() => {
    if (!loading) {
      initializeServiceWorker({
        disabled: !value,
      });
    }
  }, [loading, value]);

  return <>{children}</>;
};
